import classNames from "classnames/bind";
import { debounce } from "es-toolkit/compat";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../Button/Button";
import TextInputBare from "../DataInput/TextInput/TextInput";
import { Drawer } from "../Drawer";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { KeyValueList, KeyValueListProps } from "../KeyValueList";
import type { KeyValuePair } from "../KeyValueList/types";
import Panel from "../Panel/Panel";
import type { SortingType } from "../utils/component-props";
import { extractDeepestChildText } from "../utils/dom";
import { useSearchDOM } from "../utils/hooks/useSearchDOM";
import { useSort } from "../utils/hooks/useSort";
import styles from "./KeyValuePanel.module.scss";
import {
  generateIsDisplayActionRow,
  getNumColumnsAndVariantBasedOnWidth,
  splitIntoNArrays,
} from "./keyValuePanelUtils";
import type { KeyValuePanelProps } from "./types";

const cx = classNames.bind(styles);

const sortPredicate = (
  items: Array<KeyValuePair & { titleAsString: string }>,
  config: SortingType
) => {
  return items
    .slice()
    .sort((a, b) =>
      config.sortDirection === "asc"
        ? a.titleAsString.localeCompare(b.titleAsString)
        : b.titleAsString.localeCompare(a.titleAsString)
    );
};

/**
 * Panel that contains KeyValueLists and optionally a drawer to display the full contents
 */

const KeyValuePanel = ({
  data = [],
  panelMaxColumnCount = 3,
  drawerKeyValueListProps = {},
  panelKeyValueListProps = {},
  panelKeyValueListContainerProps,
  panelProps = {},
  drawerProps = {},
  displayActionRowCondition = "onExtra",
  isHidden = false,
}: KeyValuePanelProps) => {
  const drawerKeyValueListPropsWithDefaults: Omit<KeyValueListProps, "data"> = {
    variant: "border",
    ...drawerKeyValueListProps,
  };
  const panelPropsWithDefaults = { title: "Details", ...panelProps };
  const drawerPropsWithDefaults = { title: "Details", ...drawerProps };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [width, setWidth] = useState();
  const containerRef = useRef(null);
  const simplifiedData = useMemo<
    Array<KeyValuePair & { titleAsString: string }>
  >(
    () =>
      data.map((d) => ({
        ...d,
        titleAsString:
          typeof d.title === "string"
            ? d.title
            : extractDeepestChildText(d.title),
      })),
    [data]
  );
  const [sortDirection, setSortDirection] =
    useState<SortingType["sortDirection"]>();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    onContainerRefChange,
    total: totalSearchableItems,
    totalHidden: totalHiddenSearchableItems,
  } = useSearchDOM({
    searchQuery,
    containerQuery: "",
    searchByQueries: [
      { type: "element-text", query: "div:first-child" },
      { type: "element-text", query: "div:last-child" },
    ],
  });
  const { data: sortedData } = useSort(
    simplifiedData,
    {
      sortDirection,
      sortBy: "titleAsString",
    },
    sortPredicate
  );

  const actionRow = (
    <Button onClick={() => setIsDrawerOpen(true)}>See More</Button>
  );

  const [panelKeyValueVariant, panelColumnCount] =
    getNumColumnsAndVariantBasedOnWidth(
      width,
      panelMaxColumnCount,
      panelKeyValueListProps?.variant
    );

  const columns = useMemo(
    () =>
      splitIntoNArrays(
        data,
        panelColumnCount,
        panelKeyValueListProps.maxRowCount
      ),
    [width, data]
  );

  const isDisplayActionRow = generateIsDisplayActionRow(
    data,
    displayActionRowCondition,
    panelKeyValueListProps.maxRowCount,
    columns.length
  );

  useEffect(() => {
    // https://stackoverflow.com/questions/58222004/how-to-get-parent-width-height-in-react-using-hooks
    const resizeObserver = new ResizeObserver(
      debounce((event) => {
        setWidth(event[0].contentBoxSize[0].inlineSize);
      }, 100)
    );

    if (containerRef?.current) {
      resizeObserver.observe(containerRef.current);
    }
  }, [containerRef]);

  return (
    <>
      <Panel
        actionRow={isDisplayActionRow ? actionRow : undefined}
        {...panelPropsWithDefaults}
        isHidden={isHidden}
      >
        <div
          {...panelKeyValueListContainerProps}
          ref={containerRef}
          className={cx(
            "container",
            panelKeyValueListContainerProps?.className
          )}
        >
          {columns.map((arr, idx) => (
            <KeyValueList
              key={idx}
              variant={panelKeyValueVariant}
              data={arr}
              {...panelKeyValueListProps}
            />
          ))}
        </div>
      </Panel>
      {isDisplayActionRow ? (
        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          {...drawerPropsWithDefaults}
        >
          <div className={cx("search-container")}>
            <TextInputBare
              name="search"
              type="search"
              placeholder="Search"
              value={searchQuery}
              onChange={setSearchQuery}
            />
            <Button
              variant="outline"
              size="x-small"
              slotLeft={
                <Icon
                  variant={
                    sortDirection === undefined
                      ? "arrows-down-up"
                      : sortDirection === "asc"
                        ? "sort-ascending"
                        : "sort-descending"
                  }
                />
              }
              className={cx("sort-button", {
                "is-active": sortDirection !== undefined,
              })}
              title={
                sortDirection === undefined
                  ? "Sort ascending"
                  : sortDirection === "asc"
                    ? "Sort descending"
                    : "Clear sort"
              }
              aria-label={
                sortDirection === undefined
                  ? "No sort applied. Click to sort ascending."
                  : sortDirection === "asc"
                    ? "Sorted ascending. Click to sort descending."
                    : "Sorted descending. Click to clear sort."
              }
              onClick={() => {
                if (sortDirection === "asc") {
                  setSortDirection("desc");
                } else if (sortDirection === "desc") {
                  setSortDirection(undefined);
                } else {
                  setSortDirection("asc");
                }
              }}
            >
              {sortDirection === "asc"
                ? "A-Z"
                : sortDirection === "desc"
                  ? "Z-A"
                  : "Sort"}
            </Button>
          </div>
          <p
            className={cx("search-results-count")}
            style={{ visibility: searchQuery ? "visible" : "hidden" }}
          >
            Showing {totalSearchableItems - totalHiddenSearchableItems} of{" "}
            {totalSearchableItems} items
          </p>
          <KeyValueList
            applySmallFont
            data={sortedData}
            {...drawerKeyValueListPropsWithDefaults}
            ref={onContainerRefChange}
          />
          {totalSearchableItems === totalHiddenSearchableItems && (
            <EmptyState
              title="No data found"
              suggestion="Try a different search"
              icon={<Icon variant="magnifying-glass" />}
            />
          )}
        </Drawer>
      ) : null}
    </>
  );
};

export default KeyValuePanel;
